import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { FilmstripSidebar, type FilmstripSidebarHandle } from "@/components/filmstrip-sidebar";
import type { InsertPagesData } from "@/components/page-upload-dialog";
import { MainViewingArea } from "@/components/main-viewing-area";
import { UploadSection } from "@/components/upload-section";
import { CompareView } from "@/components/compare-view";
import { PageNumberWithComments } from "@/components/page-number-with-comments";
import { ActionDrawer } from "@/components/action-drawer";
import { PageStarRating } from "@/components/page-star-rating";
import { PageCommentsSection } from "@/components/page-comments-section";
import { StatusFilterDropdown } from "@/components/status-filter-dropdown";
import { useProcessingStatus } from "@/hooks/use-processing-status";
import { usePageViewTracker } from "@/hooks/use-page-view-tracker";
import { ProcessingSkeleton } from "@/components/processing-skeleton";
import { OnboardingOverlay } from "@/components/onboarding-overlay";
import { ColumnSelector } from "@/components/column-selector";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { invalidateIndicatorCaches } from "@/lib/cache-invalidation";
import type { Document, DocumentVersion, DocumentPage, PageIndicator, IndicatorComment, DocumentUser } from "@shared/schema";

interface DocumentViewerProps {
  document: Document;
  initialVersionId?: string | null;
  initialVersionIdB?: string | null;
  initialPageNumber?: number;
  initialIndicatorNumber?: number | null;
  initialMode?: "comment" | "browse" | "compare";
  compareSubMode?: "side-by-side" | "diff-heatmap";
  currentUser: { id: string; name: string; email: string } | null;
  documentUsers?: DocumentUser[];
  onboardingState?: {
    showOnboarding: boolean;
    dismissOnboarding: () => void;
  };
}





export function DocumentViewer({ 
  document, 
  initialVersionId = null, 
  initialVersionIdB = null, 
  initialPageNumber = 1, 
  initialIndicatorNumber = null,
  initialMode = "comment", 
  compareSubMode = "side-by-side",
  currentUser,
  documentUsers = [],
  onboardingState
}: DocumentViewerProps) {
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(initialVersionId);
  const [selectedVersionIdB, setSelectedVersionIdB] = useState<string | null>(initialVersionIdB);
  const [currentPageId, setCurrentPageId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"comment" | "browse" | "compare">(initialMode);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set(['open', 'inProgress', 'resolved', 'skipped']));
  const [gridColumns, setGridColumns] = useState<number>(() => {
    const saved = localStorage.getItem('browseGridColumns');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [, setLocation] = useLocation();
  
  const handleColumnsChange = useCallback((columns: number) => {
    setGridColumns(columns);
    localStorage.setItem('browseGridColumns', columns.toString());
  }, []);
  
  // Track last seen initialPageNumber from props to detect actual prop changes
  const lastSeenInitialPageNumberRef = useRef<number>(initialPageNumber);
  // Track our internally driven page number to prevent loops
  const internalPageNumberRef = useRef<number>(initialPageNumber);
  
  // Handler for low rating indicators from MainViewingArea
  const lowRatingHandlerRef = useRef<((rating: number, position: { x: number; y: number }) => void) | null>(null);
  
  // Ref for FilmstripSidebar to control scroll and flash
  const filmstripRef = useRef<FilmstripSidebarHandle>(null);
  
  // Callback to receive the handler from MainViewingArea
  const setLowRatingHandler = useCallback((handler: (rating: number, position: { x: number; y: number }) => void) => {
    lowRatingHandlerRef.current = handler;
  }, []);

  // Update indicator status mutation for PageCommentsSection
  const updateIndicatorStatusMutation = useMutation({
    mutationFn: async ({ indicatorId, status }: { indicatorId: string; status: string }) => {
      const response = await fetch(`/api/indicators/${indicatorId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status }),
      });
      if (!response.ok) throw new Error("Failed to update indicator status");
      return response.json();
    },
    onMutate: async ({ indicatorId, status }) => {
      if (!currentPageId || !currentVersion) return { previousData: {} };

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/pages", currentPageId, "indicators"] });
      await queryClient.cancelQueries({ queryKey: ["/api/pages", currentPageId, "indicators-with-comments"] });
      if (showAllComments && currentVersion.id) {
        await queryClient.cancelQueries({ queryKey: ["/api/versions", currentVersion.id, "indicators-with-comments"] });
      }

      // Snapshot previous values
      const previousIndicators = queryClient.getQueryData(["/api/pages", currentPageId, "indicators"]);
      const previousIndicatorsWithComments = queryClient.getQueryData(["/api/pages", currentPageId, "indicators-with-comments"]);
      const previousVersionIndicators = showAllComments && currentVersion.id 
        ? queryClient.getQueryData(["/api/versions", currentVersion.id, "indicators-with-comments"])
        : undefined;

      // Optimistically update indicators cache
      queryClient.setQueryData(["/api/pages", currentPageId, "indicators"], (old: any) => {
        if (!old) return old;
        return old.map((indicator: any) => 
          indicator.id === indicatorId ? { ...indicator, status } : indicator
        );
      });

      // Optimistically update indicators-with-comments cache
      queryClient.setQueryData(["/api/pages", currentPageId, "indicators-with-comments"], (old: any) => {
        if (!old) return old;
        return old.map((indicator: any) => 
          indicator.id === indicatorId ? { ...indicator, status } : indicator
        );
      });

      // Optimistically update version-level cache if showing all pages
      if (showAllComments && currentVersion.id) {
        queryClient.setQueryData(["/api/versions", currentVersion.id, "indicators-with-comments"], (old: any) => {
          if (!old) return old;
          return old.map((indicator: any) => 
            indicator.id === indicatorId ? { ...indicator, status } : indicator
          );
        });
      }

      return { 
        previousIndicators, 
        previousIndicatorsWithComments,
        previousVersionIndicators,
        currentPageId,
        versionId: currentVersion.id
      };
    },
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.currentPageId) {
        if (context.previousIndicators) {
          queryClient.setQueryData(["/api/pages", context.currentPageId, "indicators"], context.previousIndicators);
        }
        if (context.previousIndicatorsWithComments) {
          queryClient.setQueryData(["/api/pages", context.currentPageId, "indicators-with-comments"], context.previousIndicatorsWithComments);
        }
        if (context.previousVersionIndicators && context.versionId) {
          queryClient.setQueryData(["/api/versions", context.versionId, "indicators-with-comments"], context.previousVersionIndicators);
        }
      }
    },
    onSettled: () => {
      // Invalidate all relevant caches to ensure server sync
      if (currentPageId && currentVersion) {
        invalidateIndicatorCaches(
          currentPageId,
          document.id,
          currentVersion.version,
          currentVersion.id
        );
      }
    }
  });

  // Update document color mutation
  const updateDocumentColorMutation = useMutation({
    mutationFn: async (color: string) => {
      const response = await fetch(`/api/documents/${document.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ defaultIndicatorColor: color }),
      });
      if (!response.ok) throw new Error("Failed to update document color");
      return response.json();
    },
    onMutate: async (color: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["/api/documents", document.id] });
      
      // Snapshot the previous value
      const previousDocument = queryClient.getQueryData(["/api/documents", document.id]);
      
      // Optimistically update the document
      queryClient.setQueryData(["/api/documents", document.id], (old: any) => {
        if (!old) return old;
        return { ...old, defaultIndicatorColor: color };
      });
      
      return { previousDocument };
    },
    onSuccess: (data) => {
      // Update the local document state immediately
      queryClient.setQueryData(["/api/documents", document.id], data);
      // Also invalidate to ensure server state sync
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
    },
    onError: (error, variables, context) => {
      // Roll back on error
      if (context?.previousDocument) {
        queryClient.setQueryData(["/api/documents", document.id], context.previousDocument);
      }
    },
  });

  // Reorder pages mutation
  const reorderPagesMutation = useMutation({
    mutationFn: async (updates: { pageId: string; displayOrder: number }[]) => {
      console.log('🚀 Reorder mutation starting with updates:', updates);
      const response = await fetch(`/api/pages/reorder`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      console.log('📡 Reorder response status:', response.status, response.ok);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Reorder failed:', errorText);
        throw new Error(`Failed to reorder pages: ${errorText}`);
      }
      const result = await response.json();
      console.log('✅ Reorder success:', result);
      return result;
    },
    onMutate: async (updates) => {
      if (!currentVersion) return { previousData: null, queryKey: null };
      
      const queryKey = ["/api/documents", document.id.toString(), "versions", currentVersion.version.toString(), "pages"];
      await queryClient.cancelQueries({ queryKey });
      
      const previousData = queryClient.getQueryData(queryKey);
      
      // Optimistically update pages order sorted by displayOrder
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        const updatedPages = [...old.pages];
        updates.forEach(({ pageId, displayOrder }) => {
          const pageIndex = updatedPages.findIndex(p => p.id === pageId);
          if (pageIndex !== -1) {
            updatedPages[pageIndex] = { ...updatedPages[pageIndex], displayOrder };
          }
        });
        // Sort by displayOrder (array position is source of truth for page numbers)
        const sortedPages = updatedPages.sort((a, b) => a.displayOrder - b.displayOrder);
        return { ...old, pages: sortedPages };
      });
      
      return { previousData, queryKey };
    },
    onError: (error, variables, context) => {
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },
    onSuccess: (data, variables, context) => {
      // Server returns { pages, processingStatus } - ensure pages are sorted by displayOrder
      if (context?.queryKey && data?.pages) {
        const sortedData = {
          ...data,
          pages: [...data.pages].sort((a, b) => a.displayOrder - b.displayOrder)
        };
        queryClient.setQueryData(context.queryKey, sortedData);
        
        // currentPage is derived from currentPageId via array index - no manual update needed
        // URL update handled by dedicated effect
      }
    },
  });

  // Toggle page visibility mutation
  const togglePageVisibilityMutation = useMutation({
    mutationFn: async ({ pageId, hidden }: { pageId: string; hidden: boolean }) => {
      console.log('🔄 MUTATION togglePageVisibility starting');
      console.log('   pageId:', pageId);
      console.log('   hidden:', hidden);
      const response = await fetch(`/api/pages/${pageId}/visibility`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hidden }),
      });
      console.log('   response status:', response.status, response.ok);
      if (!response.ok) throw new Error("Failed to toggle page visibility");
      const data = await response.json();
      console.log('   response data:', data);
      return data;
    },
    onMutate: async ({ pageId, hidden }) => {
      const queryKey = ["/api/documents", document.id.toString(), "versions", currentVersion?.version?.toString(), "pages"];
      await queryClient.cancelQueries({ queryKey });
      
      const previousPages = queryClient.getQueryData(queryKey);
      
      // Optimistically update page visibility only
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        const updatedPages = old.pages.map((p: DocumentPage) => 
          p.id === pageId ? { ...p, hidden } : p
        );
        return { ...old, pages: updatedPages };
      });
      
      return { previousPages, queryKey };
    },
    onError: (error, variables, context) => {
      if (context?.previousPages && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousPages);
      }
    },
    onSuccess: async (data, variables, context) => {
      console.log('✅ togglePageVisibility onSuccess, data:', data);
      // Force immediate refetch to get updated pages
      if (context?.queryKey) {
        await queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
    },
    onSettled: async (data, error, variables, context) => {
      console.log('🏁 togglePageVisibility onSettled');
      if (context?.queryKey) {
        await queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
    },
  });

  // Create placeholder page mutation with optimistic updates
  const createPlaceholderMutation = useMutation({
    mutationFn: async ({ versionId, insertionPosition, label }: { versionId: string; insertionPosition: number; label: string }) => {
      console.log('➕ MUTATION createPlaceholder starting');
      console.log('   versionId:', versionId);
      console.log('   insertionPosition:', insertionPosition);
      console.log('   label:', label);
      const response = await fetch(`/api/versions/${versionId}/placeholder-page`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ insertionPosition, label }),
      });
      if (!response.ok) throw new Error("Failed to create placeholder");
      const data = await response.json();
      console.log('   response:', data);
      return data;
    },
    onMutate: async ({ insertionPosition, label }) => {
      const queryKey = ["/api/documents", document.id.toString(), "versions", currentVersion?.version?.toString(), "pages"];
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData(queryKey);
      
      // Optimistically update with temporary placeholder
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old?.pages) return old;
        
        // Sort pages by displayOrder to ensure correct positioning
        const sortedPages = [...old.pages].sort((a, b) => a.displayOrder - b.displayOrder);
        
        // Find the index where we should insert (first page with displayOrder >= insertionPosition)
        // insertionPosition is a displayOrder value, not a zero-based index
        const insertAtIndex = sortedPages.findIndex(p => p.displayOrder >= insertionPosition);
        const spliceIndex = insertAtIndex === -1 ? sortedPages.length : insertAtIndex;
        
        const tempPlaceholder = {
          id: `temp-${Date.now()}`,
          versionId: currentVersion?.id || '',
          pageNumber: null,
          imageUrl: null,
          webpUrl: null,
          width: 1920,
          height: 1080,
          hidden: false,
          displayOrder: insertionPosition,
          insertedViaPartialUpload: false,
          insertedAt: null,
          isPlaceholder: true,
          label: label,
          createdAt: new Date(),
        };
        
        // Insert placeholder at the calculated splice index
        const updatedPages = [...sortedPages];
        updatedPages.splice(spliceIndex, 0, tempPlaceholder);
        
        // Update displayOrder for all pages to maintain continuity
        const reorderedPages = updatedPages.map((page, index) => ({
          ...page,
          displayOrder: index,
        }));
        
        return { ...old, pages: reorderedPages };
      });
      
      return { previousData, queryKey };
    },
    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },
    onSuccess: async (data, variables, context) => {
      console.log('✅ createPlaceholder onSuccess, data:', data);
      // Replace optimistic data with real server data
      if (context?.queryKey) {
        await queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
    },
  });

  const { data: documentWithVersions, isLoading: isLoadingDocument } = useQuery({
    queryKey: ["/api/documents", document.id.toString()],
  });

  const versions = (documentWithVersions as any)?.versions || [];
  const currentVersion = selectedVersionId 
    ? versions.find((v: DocumentVersion) => v.id === selectedVersionId)
    : versions[0]; // Default to latest version

  // Handle processing complete (defined after currentVersion to avoid initialization issues)
  const handleProcessingComplete = useCallback(async (pageIds: string[]) => {
    // Close the upload dialog first to return to main viewer (do this before any guards)
    setShowUploadDialog(false);

    if (!currentVersion) {
      console.warn('handleProcessingComplete called before currentVersion loaded');
      return;
    }

    // Cancel any in-flight page queries
    await queryClient.cancelQueries({ 
      queryKey: ["/api/documents", document.id.toString(), "versions", currentVersion.version.toString(), "pages"] 
    });

    // Scroll filmstrip to start and wait for it to fully complete
    await filmstripRef.current?.scrollToStart();

    // Refetch pages and wait for data (not just invalidate)
    await queryClient.refetchQueries({ 
      queryKey: ["/api/documents", document.id.toString(), "versions", currentVersion.version.toString(), "pages"],
      type: 'active'
    });

    // Refetch document and wait for data
    await queryClient.refetchQueries({ 
      queryKey: ["/api/documents", document.id.toString()],
      type: 'active'
    });

    // Flash the new pages
    if (pageIds.length > 0) {
      filmstripRef.current?.flashPages(pageIds);
    }
  }, [document.id, currentVersion])

  const { data: pagesResponse, isLoading: isLoadingPages, refetch: refetchPages } = useQuery({
    queryKey: ["/api/documents", document.id.toString(), "versions", currentVersion?.version?.toString(), "pages"],
    enabled: !!currentVersion,
  });

  const pages = (pagesResponse as any)?.pages || [];
  const processingStatus = (pagesResponse as any)?.processingStatus;
  
  // Derive currentPage from currentPageId position in VISIBLE pages array (single source of truth)
  const currentPage = useMemo(() => {
    if (!currentPageId || !pages.length) return initialPageNumber;
    const visiblePages = pages.filter((p: DocumentPage) => !p.hidden);
    const index = visiblePages.findIndex((p: DocumentPage) => p.id === currentPageId);
    return index >= 0 ? index + 1 : 1;
  }, [currentPageId, pages, initialPageNumber]);
  
  // Dwell-time page view tracker (3s on current page, all view modes)
  usePageViewTracker({
    documentId: document.id,
    pageId: currentPageId ?? null,
    documentUserId: currentUser?.id ?? null,
  });

  // Manually refetch pages every 2 seconds while processing
  useEffect(() => {
    if (processingStatus?.status === 'processing' || processingStatus?.status === 'pending') {
      const interval = setInterval(() => {
        refetchPages();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [processingStatus?.status, refetchPages]);
  
  // Hydrate currentPageId from initialPageNumber when pages load
  useEffect(() => {
    if (!pages.length) return;
    
    const visiblePages = pages.filter((p: DocumentPage) => !p.hidden);
    if (!visiblePages.length) return;
    
    // Clamp requested position to available pages
    const requestedIndex = initialPageNumber - 1; // Convert 1-based to 0-based
    const clampedIndex = Math.max(0, Math.min(requestedIndex, visiblePages.length - 1));
    const targetPage = visiblePages[clampedIndex];
    
    // Update currentPageId to match the target position
    if (targetPage && targetPage.id !== currentPageId) {
      setCurrentPageId(targetPage.id);
      const clampedPageNumber = clampedIndex + 1;
      lastSeenInitialPageNumberRef.current = clampedPageNumber;
      internalPageNumberRef.current = clampedPageNumber;
      
      // Only update URL if we had to clamp (position was out of bounds)
      if (clampedIndex !== requestedIndex && currentVersion && viewMode === "comment") {
        setLocation(`/doc/${document.id}/comment/v/${currentVersion.version}/p/${clampedPageNumber}`);
      }
    }
  }, [pages, initialPageNumber, currentPageId, currentVersion, viewMode, document.id, setLocation]);

  // REMOVED: URL sync effect causes issues during version transitions
  // URL updates are handled by:
  // 1. handlePageSelect (user clicks page)
  // 2. handleVersionChange (user changes version)
  // 3. Hydration effect (only when clamping to bounds)
  
  // Handle external URL changes (back button, direct links) - NOT our own URL updates
  useEffect(() => {
    if (!pages.length) return;
    
    // Only react if initialPageNumber prop actually changed (not just different from internal ref)
    if (initialPageNumber !== lastSeenInitialPageNumberRef.current) {
      lastSeenInitialPageNumberRef.current = initialPageNumber;
      
      // Only update currentPageId if this wasn't our own internal navigation
      if (initialPageNumber !== internalPageNumberRef.current) {
        const visiblePages = pages.filter((p: DocumentPage) => !p.hidden);
        const index = initialPageNumber - 1; // Convert 1-based to 0-based
        const page = visiblePages[index]; // Look up in VISIBLE pages, not full array
        if (page) {
          setCurrentPageId(page.id);
          internalPageNumberRef.current = initialPageNumber;
        }
      }
    }
  }, [initialPageNumber, pages]);

  // Processing status for browse view
  const browseProcessingStatus = useProcessingStatus(
    !isLoadingPages && pages.length === 0 && currentVersion ? currentVersion.id : null
  );

  const handleIndicatorUrlUpdate = (indicatorNumber: number | null, pageNumber?: number) => {
    if (!currentVersion || viewMode !== "comment") return;

    // If indicator is null, clean up URL immediately (dialog close)
    if (indicatorNumber === null) {
      const baseUrl = `/doc/${document.id}/comment/v/${currentVersion.version}/p/${currentPage}`;
      setLocation(baseUrl);
      return;
    }

    // Determine target page (either cross-page or current page)
    const targetPage = pageNumber || currentPage;
    
    // Build full URL with indicator in one shot
    const fullUrl = `/doc/${document.id}/comment/v/${currentVersion.version}/p/${targetPage}/c/${indicatorNumber}`;
    
    // If cross-page navigation is needed, update state first
    if (pageNumber && pageNumber !== currentPage) {
      const visiblePages = pages.filter((p: DocumentPage) => !p.hidden);
      const targetPageData = visiblePages[pageNumber - 1];
      
      if (targetPageData) {
        // Update internal tracking and state, then navigate to full URL
        internalPageNumberRef.current = pageNumber;
        setCurrentPageId(targetPageData.id);
      }
    }
    
    // Apply full URL (with indicator) in single navigation
    setLocation(fullUrl);
  };

  const handleVersionChange = (versionId: string) => {
    setSelectedVersionId(versionId);
    
    // Find the version number for URL (use version number, not ID)
    const version = versions.find((v: DocumentVersion) => v.id === versionId);
    if (version) {
      if (viewMode === "comment") {
        // Use the page number we're currently tracking
        const pageNum = internalPageNumberRef.current || initialPageNumber;
        setLocation(`/doc/${document.id}/comment/v/${version.version}/p/${pageNum}`);
      } else if (viewMode === "browse") {
        setLocation(`/doc/${document.id}/browse/v/${version.version}`);
      }
      // Compare mode has its own version handling
    }
  };

  const handlePageSelect = (pageId: string, pageNumber: number) => {
    // Update URL FIRST before state change
    if (currentVersion && viewMode === "comment") {
      setLocation(`/doc/${document.id}/comment/v/${currentVersion.version}/p/${pageNumber}`);
      internalPageNumberRef.current = pageNumber;
    }
    
    // Then update state (triggers data fetching AFTER URL is updated)
    setCurrentPageId(pageId);
  };

  // Handle mode changes with URL updates
  const handleModeChange = (newMode: "comment" | "browse" | "compare") => {
    setViewMode(newMode);
    
    if (currentVersion) {
      if (newMode === "comment") {
        setLocation(`/doc/${document.id}/comment/v/${currentVersion.version}/p/${currentPage}`);
      } else if (newMode === "browse") {
        setLocation(`/doc/${document.id}/browse/v/${currentVersion.version}`);
      } else if (newMode === "compare") {
        // For compare mode, use latest two versions as default
        const sortedVersions = versions.sort((a: DocumentVersion, b: DocumentVersion) => b.version - a.version);
        const versionA = sortedVersions[1] || sortedVersions[0]; // Second latest or latest
        const versionB = sortedVersions[0]; // Latest
        setLocation(`/doc/${document.id}/compare/v/${versionA.version}/vs/${versionB.version}`);
      }
    }
  };

  // Update state when URL parameters change
  useEffect(() => {
    if (initialVersionId !== null && initialVersionId !== selectedVersionId) {
      setSelectedVersionId(initialVersionId);
    }
  }, [initialVersionId, selectedVersionId]);

  useEffect(() => {
    if (initialVersionIdB !== null && initialVersionIdB !== selectedVersionIdB) {
      setSelectedVersionIdB(initialVersionIdB);
    }
  }, [initialVersionIdB, selectedVersionIdB]);

  useEffect(() => {
    if (initialMode !== viewMode) {
      setViewMode(initialMode);
    }
  }, [initialMode, viewMode]);

  // No longer needed: pendingIndicatorRef approach replaced with single-shot navigation

  // Validation handled by initialization effect - currentPage is derived from valid currentPageId

  if (isLoadingDocument) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-3">
      {/* Action Drawer */}
      <ActionDrawer
        document={document}
        versions={versions}
        currentVersion={currentVersion}
        viewMode={viewMode}
        onModeChange={handleModeChange}
        onVersionChange={handleVersionChange}
        onUploadNewVersion={() => setShowUploadDialog(true)}
        updateDocumentColorMutation={updateDocumentColorMutation}
      />

      {viewMode === "comment" ? (
        <>
          {/* Horizontal Filmstrip */}
          <div className="w-full">
            <FilmstripSidebar
              ref={filmstripRef}
              pages={pages}
              currentPage={currentPage}
              currentPageId={currentPageId}
              onPageSelect={handlePageSelect}
              isLoading={false}
              selectedVersion={currentVersion}
              documentColor={document.defaultIndicatorColor}
              onReorderPages={(updates) => {
                console.log('🎯 DOCUMENT-VIEWER onReorderPages callback received updates:', updates);
                reorderPagesMutation.mutate(updates);
              }}
              onTogglePageVisibility={(pageId, hidden) => togglePageVisibilityMutation.mutate({ pageId, hidden })}
              onInsertPlaceholder={(afterPosition: number, label: string) => {
                if (currentVersion) {
                  console.log('➕ Inserting placeholder after position:', afterPosition, 'with label:', label);
                  createPlaceholderMutation.mutate({ 
                    versionId: currentVersion.id, 
                    insertionPosition: afterPosition,
                    label
                  });
                }
              }}
              isReordering={reorderPagesMutation.isPending}
            />
          </div>

          {/* Main Viewing Area */}
          <div className="w-full">
            <MainViewingArea
              document={document}
              versions={versions}
              currentVersion={currentVersion}
              pages={pages}
              currentPage={currentPage}
              currentPageId={currentPageId}
              onVersionChange={handleVersionChange}
              onPageChange={handlePageSelect}
              isLoading={false}
              viewMode={viewMode}
              currentUser={currentUser}
              onLowRatingIndicator={setLowRatingHandler}
              initialIndicatorNumber={initialIndicatorNumber}
              onIndicatorChange={handleIndicatorUrlUpdate}
              isReordering={reorderPagesMutation.isPending}
              isTogglingVisibility={togglePageVisibilityMutation.isPending}
            />
          </div>

          {/* Comment Filter Toggle - New section below main viewing area */}
          {pages.length > 0 && (
            <div className="w-full">
              <div className="bg-card border border-border rounded-lg p-2">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <label 
                      htmlFor="comment-filter-toggle" 
                      className="text-xs text-muted-foreground cursor-pointer select-none"
                    >
                      This page only
                    </label>
                    <Switch
                      id="comment-filter-toggle"
                      checked={showAllComments}
                      onCheckedChange={setShowAllComments}
                      data-testid="toggle-comment-filter"
                      style={showAllComments ? { backgroundColor: document.defaultIndicatorColor } : {}}
                    />
                    <label 
                      htmlFor="comment-filter-toggle" 
                      className="text-xs text-muted-foreground cursor-pointer select-none"
                    >
                      All pages
                    </label>
                  </div>
                  <StatusFilterDropdown
                    selectedStatuses={selectedStatuses}
                    onStatusToggle={(status) => {
                      setSelectedStatuses(prev => {
                        const newStatuses = new Set(prev);
                        if (newStatuses.has(status)) {
                          newStatuses.delete(status);
                        } else {
                          newStatuses.add(status);
                        }
                        return newStatuses;
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Page Comments Section - Below star rating */}
          {pages.length > 0 && (
            <div className="w-full">
              <PageCommentsSection
                pageId={currentPageId || ''}
                versionId={currentVersion?.id}
                documentId={document.id}
                versionNumber={currentVersion?.version || 1}
                currentUser={currentUser}
                documentColor={document.defaultIndicatorColor}
                documentUsers={documentUsers}
                onIndicatorClick={handleIndicatorUrlUpdate}
                showAllPages={showAllComments}
                selectedStatuses={selectedStatuses}
                updateIndicatorStatusMutation={updateIndicatorStatusMutation}
              />
            </div>
          )}
        </>
      ) : viewMode === "browse" ? (
        <>
          {/* Column Selector */}
          <ColumnSelector 
            columns={gridColumns}
            onColumnsChange={handleColumnsChange}
            documentColor={document.defaultIndicatorColor}
          />

          {/* Grid Layout for Pages */}
          <div className="w-full max-w-7xl mx-auto px-4">
            {isLoadingPages ? (
              <div 
                className={`grid gap-4 ${
                  gridColumns === 1 ? 'grid-cols-1' :
                  gridColumns === 2 ? 'grid-cols-2' :
                  gridColumns === 3 ? 'grid-cols-3' :
                  'grid-cols-4'
                }`}
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="w-full aspect-[8.5/11] bg-muted animate-pulse rounded-md" />
                ))}
              </div>
            ) : browseProcessingStatus.isProcessing ? (
              <ProcessingSkeleton 
                type="browse"
                progress={browseProcessingStatus.progress}
                statusText={browseProcessingStatus.statusText}
                documentColor={document.defaultIndicatorColor}
                count={currentVersion?.pageCount || 6}
              />
            ) : (() => {
              const visiblePages = pages.filter((p: DocumentPage) => !p.hidden);
              return (
              <div 
                className={`grid gap-6 transition-all duration-500 ease-in-out ${
                  gridColumns === 1 ? 'grid-cols-1' :
                  gridColumns === 2 ? 'grid-cols-2' :
                  gridColumns === 3 ? 'grid-cols-3' :
                  'grid-cols-4'
                }`}
              >
                {visiblePages.map((page: any, index: number) => (
                  <div 
                    key={page.id} 
                    className="relative group animate-in fade-in slide-in-from-bottom-4 duration-300"
                    style={{ animationDelay: `${Math.min(index * 50, 500)}ms` }}
                  >
                    {/* Page number badge - subtle design */}
                    <div className="absolute -top-1.5 -left-1.5 md:-top-2 md:-left-2 z-10">
                      <div 
                        className="flex items-center justify-center w-5 h-5 md:w-7 md:h-7 rounded-full shadow-sm bg-zinc-700 dark:bg-zinc-600 text-[10px] md:text-xs font-medium text-white"
                      >
                        {index + 1}
                      </div>
                    </div>
                    
                    {/* Page image */}
                    <div 
                      className="relative overflow-hidden rounded-md cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => {
                        if (currentVersion) {
                          setLocation(`/doc/${document.id}/comment/v/${currentVersion.version}/p/${index + 1}`);
                        }
                      }}
                      style={{
                        aspectRatio: `${page.width}/${page.height}`,
                        ...(page.isPlaceholder ? {
                          backgroundColor: document.defaultIndicatorColor + '20',
                          border: `2px dashed ${document.defaultIndicatorColor}60`,
                        } : {
                          border: '0.5px solid hsl(var(--border) / 0.3)',
                        })
                      }}
                    >
                      {page.isPlaceholder ? (
                        <div className="flex items-center justify-center w-full h-full p-4">
                          <div 
                            className="text-sm md:text-base lg:text-lg font-bold text-center leading-tight"
                            style={{ color: document.defaultIndicatorColor }}
                            title={page.label || "New Slide"}
                          >
                            {page.label || "NEW SLIDE"}
                          </div>
                        </div>
                      ) : (
                        <img
                          src={page.webpUrl}
                          alt={`Page ${index + 1}`}
                          className="w-full h-auto transition-transform duration-300"
                          data-testid={`img-page-${index + 1}`}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              );
            })()}
          </div>
        </>
      ) : (
        <>
          {/* Compare View */}
          <CompareView
            document={document}
            versions={versions}
            currentVersionId={currentVersion?.id.toString() || null}
            onVersionChange={handleVersionChange}
          />
        </>
      )}
      
      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="w-[calc(100vw-16px)] max-w-2xl max-h-[90vh] p-0 sm:w-full rounded-lg overflow-hidden">
          <VisuallyHidden>
            <DialogTitle>Upload New Version</DialogTitle>
            <DialogDescription>Upload a new version of this document</DialogDescription>
          </VisuallyHidden>
          <UploadSection
            existingDocumentId={document.id}
            documentColor={document.defaultIndicatorColor}
            onUploadComplete={() => setShowUploadDialog(false)}
            onNavigateToVersion={(documentId, versionNumber) => {
              setShowUploadDialog(false);
              setLocation(`/doc/${documentId}/comment/v/${versionNumber}/p/1`);
            }}
            onPagesInserted={() => setShowUploadDialog(false)}
            onProcessingComplete={handleProcessingComplete}
          />
        </DialogContent>
      </Dialog>

      {/* Onboarding Overlay */}
      {onboardingState && (
        <OnboardingOverlay
          isOpen={onboardingState.showOnboarding}
          onDismiss={onboardingState.dismissOnboarding}
          documentColor={document.defaultIndicatorColor}
        />
      )}
    </div>
  );
}
